/**
 * Motion dice: turning a hand movement into a `DiceToss` (how the 3D dice fly). Pure functions,
 * no browser APIs: the phone's motion samples and swipes come in from `motion.ts` and `DiceTray`.
 *
 * Tray axes (see `engine.ts`): x to the right, y up off the table, z toward the bottom of the
 * screen (so a throw "forward", up the screen, has negative z velocity).
 */
import { TOSS_MAX_SPEED, TOSS_MAX_SPIN, type DiceToss } from '@kadice/shared';
import { tuning } from './tuning';

/** Small seeded PRNG (mulberry32): the same seed gives the same jitter on every screen. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const newSeed = (): number => Math.floor(Math.random() * 0xffffffff);

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const round = (n: number) => Math.round(n * 100) / 100;
const cap = (v: [number, number, number], max: number): [number, number, number] => {
  const len = Math.hypot(...v);
  const k = len > max ? max / len : 1;
  return [round(v[0] * k), round(v[1] * k), round(v[2] * k)];
};

/**
 * A throw from its strength (0 to 1), its direction on the screen (x right, y up the screen;
 * any length) and extra spin. Stronger: faster, higher, more spin, so the dice bounce off the walls.
 * `loft` (-1 to 1): a lob (+, the hand moved up: much higher, less far; `lobLift`, `lobForward`)
 * or a drive (-, the hand moved down at the floor: further and flatter; `driveForward`, `driveLift`).
 * The curve is `tuning()`'s (Dice Lab); speed and spin are still capped at `TOSS_MAX_SPEED` /
 * `TOSS_MAX_SPIN` (the server checks them).
 */
export function tossOf(
  strength: number,
  dir: { x: number; y: number },
  spin: [number, number, number],
  seed: number,
  loft = 0,
): DiceToss {
  const tu = tuning();
  const s = clamp(strength, tu.minStrength, 1);
  const lob = clamp(loft, 0, 1);
  const drive = clamp(-loft, 0, 1);
  const far = 1 - tu.lobForward * lob + tu.driveForward * drive;
  const high = Math.max(0, 1 + tu.lobLift * lob - tu.driveLift * drive);
  const len = Math.hypot(dir.x, dir.y) || 1;
  const up = clamp(dir.y / len, -1, 1);
  const side = clamp(dir.x / len, -1, 1);
  // A normal flick stays about as before; the hardest flicks get much faster (the cube term).
  const speed = tu.speedBase + tu.speedLinear * s + tu.speedCubic * s ** 3;
  const v: [number, number, number] = [
    side * speed * tu.sideways * far,
    (tu.liftBase + tu.liftLinear * s) * high,
    -up * speed * far,
  ];
  // Always some tumble; the hand's own spin adds to it.
  const r = rng(seed ^ 0x5bd1e995);
  const base = tu.spinBase + tu.spinLinear * s + tu.spinCubic * s ** 3;
  const w: [number, number, number] = [
    spin[0] + (r() - 0.5) * base * 2 - base * 0.6,
    spin[1] + (r() - 0.5) * base,
    spin[2] + (r() - 0.5) * base * 2,
  ];
  return { v: cap(v, TOSS_MAX_SPEED), w: cap(w, TOSS_MAX_SPIN), s: seed >>> 0 };
}

/** One `devicemotion` reading: linear acceleration (m/s², gravity removed) and rotation rate (deg/s), device axes. */
export interface MotionSample {
  t: number;
  ax: number;
  ay: number;
  az: number;
  /** About the device's x (beta), y (gamma) and z (alpha) axes. */
  rx: number;
  ry: number;
  rz: number;
  /** Which way is up (unit vector, device axes, from gravity); missing if the device gives no gravity. */
  ux?: number;
  uy?: number;
  uz?: number;
}

/** What a hand movement threw, before it becomes a `DiceToss` (`tossOfThrow`). The Dice Lab shows it. */
export interface HandThrow {
  /** 'shake': shaken without holding the screen (`shake.ts`), then read as a flick. */
  from: 'motion' | 'swipe' | 'shake';
  /** 0 to 1 and over, not yet clamped. */
  strength: number;
  /** On the screen: x right, y up the screen. */
  dir: { x: number; y: number };
  spin: [number, number, number];
  /** Lob (+) or drive (-), -1 to 1. */
  loft: number;
  /** Popple: the bubble popped; the dice also shoot straight up (`POP_LIFT`) on top of the throw. */
  pop?: boolean;
}

/** Popple: the extra upward speed a pop gives every die (tray units/s), so they all shoot up inside the bubble. */
const POP_LIFT = 17;

/** A pop's strength over 1 (full) makes the throw that much faster still (`tossOf` stops at 1). */
export function tossOfThrow(h: HandThrow, seed: number): DiceToss {
  const toss = tossOf(h.strength, h.dir, h.spin, seed, h.loft);
  if (h.pop) {
    const k = Math.max(1, h.strength);
    toss.v = cap([toss.v[0] * k, toss.v[1] * k + POP_LIFT, toss.v[2] * k], TOSS_MAX_SPEED);
  }
  return toss;
}

/** Popple: a pop's strength, from `POP_MIN` to `POP_MAX` (over 1: faster than a full throw, `tossOfThrow`). */
const POP_MIN = 0.7;
const POP_MAX = 1.15;

/** Popple: the throw a popped bubble gives, flung in a random direction (from the throw's seed). */
export function popThrow(seed: number): HandThrow {
  const r = rng(seed ^ 0x7f4a7c15);
  const a = r() * Math.PI * 2;
  return {
    from: 'swipe',
    strength: POP_MIN + r() * (POP_MAX - POP_MIN),
    dir: { x: Math.cos(a), y: Math.sin(a) },
    spin: [(r() - 0.5) * 10, (r() - 0.5) * 10, (r() - 0.5) * 10],
    loft: r() * 0.7 - 0.2,
    pop: true,
  };
}

/** The harder of a phone flick and a swipe: on a phone either one throws (user, 2026-10-16). */
export const stronger = (a: HandThrow, b: HandThrow): HandThrow => (b.strength > a.strength ? b : a);

/*
 * Tuning (`tuning()`): `flickWindowMs`, how long before letting go the flick is read from;
 * `fullHandSpeed` (m/s), a full-strength throw (room above a normal flick, 2 to 3 m/s, for hard
 * ones); `stillHandSpeed` (m/s), below it the phone barely moved (the weakest throw); `loftGain`,
 * how quickly an up or down share of the flick becomes a full lob or drive; `sideHand`, how much
 * more a sideways hand movement counts (a wrist moves less sideways than forward); `swingCredit`,
 * how much of a swing the other way just before counts (a shake: its hardest swing, not the average).
 */

type Vec = [number, number, number];
const dot = (a: Vec, b: Vec) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const unit = (a: Vec): Vec | null => {
  const l = Math.hypot(...a);
  return l > 1e-3 ? [a[0] / l, a[1] / l, a[2] / l] : null;
};

/**
 * Read the phone's motion just before you let go: the acceleration over the last `flickWindowMs`
 * is summed into the hand's speed, and its fastest moment sets the strength and direction. Where
 * the device gives gravity, that moment is turned into the real world, however the phone is held:
 * across (sideways on the tray), away from you (up the tray) and up or down (`loft`: toward the
 * sky lobs, at the floor drives). Moving it toward you still throws up the tray, weakly. Without
 * gravity, device axes: along the screen's top edge or away from the screen is up the tray.
 * `shake`: how much you shook the dice first (0 to 1), adding the phone's spin.
 */
export function readMotion(samples: readonly MotionSample[], releasedAt: number, shake: number): HandThrow {
  const tu = tuning();
  const recent = samples.filter((s) => s.t >= releasedAt - tu.flickWindowMs && s.t <= releasedAt);
  const vel: Vec[] = [[0, 0, 0]];
  let v: Vec = [0, 0, 0];
  let best = { speed: 0, i: 0 };
  let spin = { rx: 0, ry: 0, rz: 0, mag: 0 };
  for (let i = 1; i < recent.length; i++) {
    const a = recent[i]!;
    const dt = Math.min(0.1, (a.t - recent[i - 1]!.t) / 1000);
    v = [v[0] + a.ax * dt, v[1] + a.ay * dt, v[2] + a.az * dt];
    vel.push(v);
    const speed = Math.hypot(...v);
    if (speed > best.speed) best = { speed, i };
    const mag = Math.hypot(a.rx, a.ry, a.rz);
    if (mag > spin.mag) spin = { rx: a.rx, ry: a.ry, rz: a.rz, mag };
  }
  const deg = Math.PI / 180;
  const k = 1 - tu.shakeSpin + tu.shakeSpin * clamp(shake, 0, 1);
  const handSpin: Vec = [spin.rx * deg * k, spin.rz * deg * k, -spin.ry * deg * k];
  const peak = vel[best.i]!;
  const along = unit(peak);
  if (!along) return { from: 'motion', strength: 0, dir: { x: 0, y: 1 }, spin: handSpin, loft: 0 };
  // The fastest swing the other way before the peak (a shake): part of it counts too.
  let back = 0;
  for (let i = 0; i <= best.i; i++) back = Math.max(back, -dot(vel[i]!, along));
  const at = recent[best.i];
  const up = at && at.ux !== undefined ? unit([at.ux, at.uy!, at.uz!]) : null;
  // Level directions: right is the screen's x made level, away from you at right angles to it.
  const right = up ? unit([1 - up[0] * up[0], -up[0] * up[1], -up[0] * up[2]]) : null;
  let across: number;
  let away: number;
  let rise = 0;
  if (up && right) {
    const fwd: Vec = [
      up[1] * right[2] - up[2] * right[1],
      up[2] * right[0] - up[0] * right[2],
      up[0] * right[1] - up[1] * right[0],
    ];
    across = dot(peak, right);
    away = dot(peak, fwd);
    rise = dot(peak, up);
  } else {
    across = peak[0];
    away = Math.max(peak[1], 0) + Math.max(-peak[2], 0) - Math.max(-peak[1], 0);
  }
  const hand = Math.hypot(across * tu.sideHand, away, rise);
  const speed = hand + tu.swingCredit * back * (hand / best.speed);
  const strength = (speed - tu.stillHandSpeed) / Math.max(0.01, tu.fullHandSpeed - tu.stillHandSpeed);
  // Toward you still goes up the tray (weakly); a straight lob or drive still goes forward a little.
  const dir = {
    x: across * tu.sideHand,
    y: Math.max(away, 0) + Math.max(-away, 0) * 0.3 + Math.abs(rise) * 0.25,
  };
  const loft = clamp((rise / best.speed) * tu.loftGain, -1, 1);
  return { from: 'motion', strength, dir, spin: handSpin, loft };
}

/** A throw from the phone's motion just before you let go (`readMotion`). */
export const tossFromMotion = (
  samples: readonly MotionSample[],
  releasedAt: number,
  shake: number,
  seed: number,
): DiceToss => tossOfThrow(readMotion(samples, releasedAt, shake), seed);

/** Read a swipe across the tray: its speed and direction at release (`fullSwipe` CSS px per ms is full strength). */
export function readSwipe(vx: number, vy: number, shake: number, seed: number): HandThrow {
  const speed = Math.hypot(vx, vy);
  const r = rng(seed);
  const extra = 10 * clamp(shake, 0, 1);
  const spin: Vec = [(r() - 0.5) * extra, (r() - 0.5) * extra, (r() - 0.5) * extra];
  const dir = speed > 0.05 ? { x: vx, y: -vy } : { x: 0, y: 1 };
  return { from: 'swipe', strength: speed / tuning().fullSwipe, dir, spin, loft: 0 };
}

/** A throw from a swipe (`readSwipe`). */
export const tossFromSwipe = (vx: number, vy: number, shake: number, seed: number): DiceToss =>
  tossOfThrow(readSwipe(vx, vy, shake, seed), seed);

/** A made-up throw for a roll that came without one (a bot, or a player on classic dice): the same on every screen. */
export function tossFromSeq(seq: number, playerId: number): DiceToss {
  const seed = (Math.imul(seq, 2654435761) ^ Math.imul(playerId, 40503)) >>> 0;
  const r = rng(seed);
  const tu = tuning();
  return tossOf(tu.botStrengthMin + r() * tu.botStrengthRange, { x: (r() - 0.5) * 1.2, y: 1 }, [0, 0, 0], seed);
}
